import _ from 'lodash'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import NetworkCard from '@/components/ui/cards/NetworkCard'

import { useGetJoinedNetworksQuery } from '@/core/services/network'

import { isConsultationOrder, isExpertiseOrder } from '@/utils/type-guards'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import TextInput from '@/components/ui/inputs/TextInput'
import { useDebouncedCallback } from 'use-debounce'

export default function JoinedNetworks() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const authUser = useAppSelector(selectAuthUser)
  const isMobile = useIsMobile()

  const cacheKey = useRef(Date.now())

  const { networks } = useGetJoinedNetworksQuery(
    { cacheKey: cacheKey.current.toString(), skip: 0 },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data: networks }) => ({
        networks: networks?.map((network) => ({
          id: network._id,
          coverPhoto: network.coverPhoto?.url,
          memberCount: network.members.length,
          name: network.name,
          pendingMembers: _.map(network.pendingMembers, '_id'),
          pendingOrders: [
            {
              count: _.filter(network.pendingOrders, isConsultationOrder).length,
              type: 'consultation' as const,
            },
            {
              count: _.filter(network.pendingOrders, isExpertiseOrder).length,
              type: 'expertise' as const,
            },
          ],
          privacy: network.privacy,
          isAdmin: network.createdBy._id === authUser?._id,
        })),
      }),
    }
  )
  const [searchQuery, setSearchQuery] = useState('')

  const debouncedOnChange = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value.toLowerCase())
  }, 300)
  
  const filteredNetworks = networks?.filter(network => 
    network.name.toLowerCase().includes(searchQuery)
  )

  return (
    <Stack px={isMobile ? 4 : 8} py={isMobile ? 2 : 4} spacing={3} width="100%">
      <Stack direction="row">
        <Typography component="h2" sx={{ fontSize: 30, fontWeight: 700 }}>
          {t('page.networks.joins.title')}
        </Typography>
      </Stack>
      {isMobile &&
        <Grid item xs={12} lg={8} xl={9}>
          <TextInput fullWidth onChange={debouncedOnChange} placeholder={t('page.networks.searchInput.placeholder')} />
        </Grid>}
      <Box>
        <Grid container spacing={4}>
          {_.map(filteredNetworks, (network) => (
            <Grid key={network.id} item xs={12} lg={6} xl={4}>
              <NetworkCard
                network={network}
                onSelect={() => {
                  navigate(`/networks/${network.id}`)
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  )
}
